import * as React from 'react';
import * as intl from 'react-intl-universal';
import { Modal } from 'antd';
import Button from 'common/components/Button';
import { ModalProps } from 'antd/lib/modal';
import { ButtonProps } from 'antd/lib/button';

interface iState {
    visible: boolean;
}

export interface ModalHelper {
    close?: any;
    open?: any;
    visible?: boolean;
    renderModal?: any;
    width: any;
    locale: any;
}

interface iProps extends Partial<ModalProps> {
    size?: 'small' | 'large' | 'middle' | number;
    footer?: React.ReactNode;
    auto?: boolean;
    triggerText: React.ReactNode;
    triggerStyle?: Object;
    onOpen?: Function;
    onClose?: Function;
    onOk?: (...args: Array<any>) => any;
    button?: Partial<ButtonProps>;
}

interface State {
    visible: boolean;
    isLoading: boolean;
}

export class ModalCore extends React.Component<iProps, State> {
    private mounted: boolean;
    static defaultProps = {
        auto: false
    };

    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: false,
            visible: false
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.props.visible && this.openHandler();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // 模态框宽度
    private get width() {
        const { size } = this.props;
        if (size && typeof size === 'string') return {
            small: 520,
            middle: 720,
            large: 920
        }[size];
        return size;
    }

    // async wrapper
    private asyncWrap = (api: Function) => async (): Promise<any> => {
        try {
            if (api) await api();
        } catch (err) {
            console.warn(err.message);
            this.mounted && this.setState({ isLoading: false });
        }
    };

    // open modal
    private openHandler = () => {
        if (!this.mounted || this.state.isLoading) return;
        const { onOpen } = this.props;
        this.setState({ visible: true, isLoading: !!onOpen }, this.asyncWrap(onOpen));
    };

    // 模态框确认回调
    private okHandler = () => {
        if (!this.mounted || this.state.isLoading) return;
        this.setState({ isLoading: true }, this.asyncWrap(async () => {
            const { onOk, onClose } = this.props;
            if (onOk) await onOk();
            if (onClose) await onClose();
            this.mounted && this.setState({ isLoading: false, visible: false });
        }));
    };

    // 模态框关闭回调
    private cancelHandler = () => {
        if (!this.mounted || this.state.isLoading) return;
        const { onClose } = this.props;
        this.setState({ visible: false, isLoading: !!onClose }, this.asyncWrap(onClose));
    };

    render() {
        const { triggerText, title, okText, cancelText, button, children, footer, triggerStyle, closable } = this.props;
        const { visible, isLoading } = this.state;
        return [
            !!triggerText && (
                !button
                    ? <a key="trigger" style={triggerStyle} onClick={this.openHandler}>{triggerText}</a>
                    : <Button key="trigger"{...button as any} style={triggerStyle}
                              onClick={this.openHandler}>{triggerText}</Button>
            ),
            <Modal key="modal" width={this.width} closable={closable} destroyOnClose visible={visible} title={title}
                  okText={okText} cancelText={cancelText} footer={isLoading ? null : footer}
                  onOk={this.okHandler} onCancel={this.cancelHandler}>
                {children}
            </Modal>
        ];
    }
}

/**
 * @deprecated
 */
export default (WrappedComponent: React.ComponentClass<any>): any =>
    class ModalWrapper extends React.Component {
        state: iState = {
            visible: false
        };

        locale: { [key: string]: string } = {
            cancelText: intl.get('Global.Cancel'),
            okText: intl.get('Global.Yes')
        };
        // size
        width = {
            small: 520,
            middle: 720,
            large: 920
        };

        // open the modal
        open = () => {
            this.setState({
                visible: true
            });
        };

        // close the modal
        close = () => {
            this.setState({
                visible: false
            });
        };

        // render modal
        renderModal = (props: Partial<ModalProps>) => (children: React.ReactElement<any>) => <Modal {...({
            onOk: this.open,
            onCancel: this.close,
            visible: this.state.visible,
            maskClosable: false,
            ...this.locale,
            ...props
        }) as any}>{children}</Modal>;

        render() {
            const modal = {
                width: this.width,
                visible: this.state.visible,
                open: this.open,
                close: this.close,
                locale: this.locale,
                renderModal: this.renderModal
            };
            // pass the modal object to the wrappedComponent
            return <WrappedComponent {...this.props} locale={this.locale} modal={modal}/>;
        }
    };
